import styles from './PerksSection.module.scss';

import React, { useCallback, useMemo } from 'react';
import cx from 'clsx';
import { useTranslation } from 'react-i18next';
import { usePerks } from 'hooks/context/Perks';
import { useInvoiceSummary } from 'routes/Checkout/context/InvoiceSummary';
import { PerkCard } from './PerkCard';
import { useMerchant } from 'hooks/context/merchant';

export type PerksSectionProps = React.HTMLProps<HTMLUListElement>& {children?: never};

export const PERKS_SECTION = {
  root: 'perks-section'
};

export const PerksSection: React.FC<PerksSectionProps> = ({
  className, ...props
}) => {
  const { t } = useTranslation();
  const { isPerksEnabled } = useMerchant();
  const { availablePerks } = usePerks();
  const { invoiceSummary, selectedPerk, setSelectedPerk } = useInvoiceSummary();

  const applicablePerks = useMemo(() => {
    const perkUuids = new Set(invoiceSummary?.applicablePerks);

    return availablePerks.filter(perk => perkUuids.has(perk.uuid));
  }, [availablePerks, invoiceSummary?.applicablePerks]);

  const toggleOrSelectPerk = useCallback((uuid: string) => {
    if (!setSelectedPerk) return null;
    if (uuid === selectedPerk) {
      setSelectedPerk(undefined);

      return;
    }
    setSelectedPerk(uuid);
  }, [selectedPerk, setSelectedPerk]);

  if (applicablePerks.length === 0 || !isPerksEnabled) return null;

  return (
    <section className={cx(styles.wrapper, className)} data-testid={PERKS_SECTION.root} {...props}>
      <h3 className={cx('h6')} id={'available-perks-id'}>{t('perks.availablePerks')}</h3>
      <div aria-labelledby={'available-perks-id'}>
        {applicablePerks.map((perk) => (
          <PerkCard
            key={perk.uuid}
            perk={perk}
            applied={selectedPerk === perk.uuid}
            disabled={!!selectedPerk}
            onClick={() => toggleOrSelectPerk(perk.uuid)}
            isClickable
          />
        ))}
      </div>
    </section>
  );
};
